/**
 * useCombineGenerate — `POST /imports/combine` mutation (Pattern 3: enqueue +
 * poll). On success navigates straight to the new combined list's Import
 * Detail page — the same "navigate on 202, then let Detail's own poll pick
 * up the parse" shape `NewImportPage`'s submit flow uses. No Compare starts
 * there: the new list inherits its sources' Thrive results (epic
 * 2026-10-01-combine-stability-perf, sub-task 01).
 *
 * Safe to retry (task 2026-10-06-combine-generate-idempotent):
 * every Generate carries a `request_id` the server uses as the new list's
 * id, so a repeat of it never makes a second list. When the answer is lost
 * (a network error, which is also what a CORS-less 5xx from the edge looks
 * like, or a 502/503/504 without the API's own detail), the list may exist
 * anyway: the hook first reads `GET /imports/{request_id}` and navigates to
 * it if it is there; otherwise it sends the same request once more. If that
 * fails too, however, it fails with {@link GenerateUnconfirmedError}. After
 * any failure the id is kept for the next click on the same recipe, so
 * trying again is harmless.
 */
import { useMutation } from "@tanstack/react-query";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";

import {
  type CombineRequest,
  ImportsApiError,
  type ImportsClient,
} from "../../../clients/imports.client";

export interface UseCombineGenerateArgs {
  client: ImportsClient;
}

/** What a Generate settles with: the new list's id, from the 202 or from
 *  the list found after a lost answer. */
export interface GenerateResult {
  import_id: string;
}

/** A Generate whose answer was lost and whose resend failed: the list may
 *  or may not exist.
 *  Its retry reuses the same `request_id`, so it cannot make a second one. */
export class GenerateUnconfirmedError extends Error {
  constructor(public readonly lastError: unknown) {
    super("Couldn't confirm the new list was created.");
    this.name = "GenerateUnconfirmedError";
    Object.setPrototypeOf(this, GenerateUnconfirmedError.prototype);
  }
}

/** Whether a failed `POST /combine` leaves its outcome unknown. `fetch`
 *  rejects with a `TypeError` on a network error and on a response without
 *  CORS headers (the edge's own 5xx pages carry none). A readable 502/503/504
 *  without a `detail` is a gateway page too. The API's own 503 (no merge
 *  slot in time) carries a `detail` and says nothing was created, so it is a
 *  definite answer, not a lost one. */
export function isLostGenerateAnswer(err: unknown): boolean {
  if (err instanceof ImportsApiError) {
    return (
      (err.status === 502 || err.status === 503 || err.status === 504) && err.detail === undefined
    );
  }
  return err instanceof TypeError;
}

/** One Generate with the recovery above. `body.request_id` must be set. */
export async function generateWithRecovery(
  client: Pick<ImportsClient, "combineGenerate" | "getImport">,
  body: CombineRequest & { request_id: string }
): Promise<GenerateResult> {
  try {
    return await client.combineGenerate(body);
  } catch (err) {
    if (!isLostGenerateAnswer(err)) throw err;
  }
  try {
    await client.getImport(body.request_id);
    return { import_id: body.request_id };
  } catch {
    // Not there (404) or not readable now: the resend below decides, and
    // with the same id it cannot make a second list.
  }
  try {
    return await client.combineGenerate(body);
  } catch (err) {
    // Whatever the resend answers, the first request may still be running
    // (an edge 504 during the merge-slot wait) and create the list: a busy
    // 503 or a 409 here says nothing about it. Keep the id (/pr-review iter 1
    // on PR #1007, Agent A §2.1 / Agent C I1).
    throw new GenerateUnconfirmedError(err);
  }
}

function newRequestId(): string {
  return crypto.randomUUID();
}

export function useCombineGenerate({ client }: UseCombineGenerateArgs) {
  const navigate = useNavigate();
  // The id of the last Generate that did not succeed, and the recipe it was
  // for: the next click on the SAME recipe sends it again. Kept after ANY
  // failure, since an earlier send of it may still be running (/pr-review
  // iter 2 on PR #1007, Agent C N1); a repeat of it can never make a second
  // list.
  const unconfirmedRef = useRef<{ recipe: string; requestId: string } | null>(null);

  return useMutation<GenerateResult, Error, CombineRequest>({
    mutationFn: async (body) => {
      const recipe = JSON.stringify({ ...body, request_id: undefined });
      const pending = unconfirmedRef.current;
      const requestId = pending && pending.recipe === recipe ? pending.requestId : newRequestId();
      unconfirmedRef.current = null;
      try {
        return await generateWithRecovery(client, { ...body, request_id: requestId });
      } catch (err) {
        unconfirmedRef.current = { recipe, requestId };
        throw err;
      }
    },
    onSuccess: (result) => {
      navigate(`/research/imports/${result.import_id}`);
    },
  });
}
